// Адаптер телеметрії станції → модель схеми.
//
// Назва файлу лишена як mockData за домовленістю, і вона чесна: весь модуль
// /scada працює на згенерованій телеметрії (scada/mock/*). Статичної копії
// цифр із референсу тут НЕМАЄ навмисно — інакше схема показувала б одне, а
// решта вкладок станції інше, і розбіжність списали б на баг.
//
// Єдине, що тут «вигадується» — поділ інверторів КТП на дві групи: у
// StationDetail інвертор знає лише свій ktpId, підгруп у даних немає. Ділимо
// рівно навпіл у порядку нумерації, як секції на реальному КТП.
import i18n from '@/i18n';
import type { StationTelemetry } from '@/scada/mock/telemetry';
import type { StationDetail, InverterUnit, KtpSection } from '@/scada/mock/stationDetail';
import type {
  SchemeModel, SchemeKtp, SchemeGroup, SchemeInverter, SchemeState, SchemeLegend, SchemeBess,
} from './types';

/** Інвертор у роботі — усе, крім аварії та відсутності зв'язку. */
const isWorking = (inv: InverterUnit) => inv.status !== 'offline' && inv.status !== 'alarm';

/**
 * Стан групи/КТП за станами дітей: аварія переважає попередження, воно —
 * норму. Свідомо НЕ піднімаємо offline до warning: вимкнений за планом
 * інвертор не є приводом фарбувати всю КТП бурштином.
 */
function rollUp(states: SchemeState[], fallback: SchemeState): SchemeState {
  if (states.some((s) => s === 'alarm')) return 'alarm';
  if (states.some((s) => s === 'warning')) return 'warning';
  if (states.length > 0 && states.every((s) => s === 'offline')) return 'offline';
  return fallback;
}

function toInverter(inv: InverterUnit, ktp: KtpSection): SchemeInverter {
  // ККД рахуємо з реальних DC/AC: ppvKw — вхід, pacKw — вихід. Для
  // непрацюючого інвертора показувати відсоток немає сенсу.
  const eff = isWorking(inv) && inv.ppvKw > 0 ? (inv.pacKw / inv.ppvKw) * 100 : null;
  return {
    id: inv.id,
    state: inv.status,
    powerKw: inv.pacKw,
    // Напруга й струм у телеметрії живуть на рівні КТП, не інвертора —
    // показуємо секційні, а не вигадуємо індивідуальні.
    voltageKv: ktp.voltageKv,
    currentA: ktp.currentA,
    tempC: inv.tempC,
    efficiencyPct: eff != null ? Math.min(100, +eff.toFixed(1)) : null,
  };
}

function buildGroups(ktp: KtpSection, kids: InverterUnit[]): SchemeGroup[] {
  const half = Math.ceil(kids.length / 2);
  const chunks = [kids.slice(0, half), kids.slice(half)].filter((c) => c.length > 0);

  return chunks.map((chunk, i) => {
    const inverters = chunk.map((inv) => toInverter(inv, ktp));
    const working = chunk.filter(isWorking).length;
    return {
      id: `${ktp.id}:g${i + 1}`,
      label: i18n.t('ak_scada2_group', { n: i + 1 }),
      ktpId: ktp.id,
      ktpName: ktp.name,
      working,
      total: chunk.length,
      state: rollUp(inverters.map((x) => x.state), working > 0 ? 'ok' : 'offline'),
      inverters,
    };
  });
}

export function buildSchemeModel(t: StationTelemetry, detail: StationDetail): SchemeModel {
  const ktps: SchemeKtp[] = detail.ktps.map((k) => {
    const kids = detail.inverters.filter((inv) => inv.ktpId === k.id);
    const groups = buildGroups(k, kids);
    return {
      id: k.id,
      name: k.name,
      state: k.status,
      working: kids.filter(isWorking).length,
      total: kids.length,
      powerKw: Math.round(kids.reduce((s, inv) => s + inv.pacKw, 0)),
      voltageKv: k.voltageKv,
      currentA: k.currentA,
      tempC: k.tempC,
      groups,
    };
  });

  // Легенда рахує КТП, а не інвертори: це верхній рівень схеми, і саме його
  // стани підсвічені на шині.
  const legend: SchemeLegend = { ok: 0, warning: 0, alarm: 0, offline: 0 };
  ktps.forEach((k) => {
    if (k.state === 'alarm') legend.alarm += 1;
    else if (k.state === 'warning') legend.warning += 1;
    else if (k.state === 'offline') legend.offline += 1;
    else legend.ok += 1;
  });

  // УЗЕ показуємо лише там, де накопичувач реально є І дає телеметрію:
  // hasBess без показань означає «встановлено, але мовчить», і малювати
  // вузол із прочерками на схемі сенсу немає.
  const bess: SchemeBess | null = t.bess
    ? {
      state: t.bess.mode === 'fault' ? 'alarm' : t.state === 'offline' ? 'offline' : 'ok',
      socPct: t.bess.socPct,
      powerKw: t.bess.powerKw,
      mode: t.bess.mode,
      modeLabel: t.bess.mode === 'charging' ? i18n.t('ak_scada2_charge')
        : t.bess.mode === 'discharging' ? i18n.t('ak_scada2_discharge')
        : t.bess.mode === 'fault' ? i18n.t('ak_scada2_alarm_2') : i18n.t('ak_scada2_standby_3'),
      sohPct: t.bess.sohPct ?? null,
    }
    : null;

  return {
    connection: {
      powerKw: t.activePowerKw,
      limitKw: t.activeLimitKw ?? null,
      freqHz: t.freqHz,
      state: t.state === 'offline' ? 'offline' : 'ok',
    },
    ktps,
    bess,
    legend,
  };
}
